import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Download, Plus, Trash2, Ship } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SeaDeliveryOrderPrint from './SeaDeliveryOrderPrint';

const DOC_TYPE = 'SEA_DELIVERY_ORDER';

const EMPTY_CONTAINER = { container_no: '', size: '', l_seal_no: '', no_of_packages: '', gross_weight: '' };

const EMPTY_FORM = {
  job_no: '', release_date: '', valid_date: '', subject: 'NOC / Delivery order',
  to: '', igm_no: '', igm_date: '',
  mbl_no: '', mbl_date: '', hbl_no: '', hbl_date: '',
  containers: [{ ...EMPTY_CONTAINER }],
  vessel_voyage: '', consignee: '', address: '', port_of_load: '', port_of_discharge: '',
  cargo_description: '', consignee_name: '', custom_broker_name: '',
};

const buildPartyText = (p) => {
  if (!p) return '';
  return [p.name, p.branch_name, p.address,
    p.phone ? `Tel: ${p.phone}` : '',
    p.email ? `Email: ${p.email}` : '']
    .filter(Boolean).join('\n');
};

const SeaDeliveryOrderForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!docketId) return;
    (async () => {
      try {
        setLoading(true);
        // Fetch print-data (resolved parties + docket)
        const docketRes = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, { headers: getAuth() });
        if (!docketRes.ok) throw new Error('Failed to fetch docket');
        const docketData = await docketRes.json();
        setDocketInfo(docketData);
        if (docketData.company) setCompanyData(docketData.company);

        // Check if SEA DO already exists
        const docRes = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`, { headers: getAuth() });
        if (docRes.ok) {
          const docData = await docRes.json();
          if (docData?.id) {
            setFormData(docData.document_data);
            setDocumentId(docData.id);
            setIsSaved(true);
            return;
          }
        }
        // No existing record — auto-populate from docket
        populateFromDocket(docketData);
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [docketId]);

  const populateFromDocket = (docketData) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload = docket.payload_json || {};
    const shipmentInfo = payload.shipment_info || {};
    const invoices = payload.invoices || [];
    const invoice = invoices[0] || {};
    const containerDetails = payload.container_details || [];

    const consigneeParty = resolved_parties?.importer_receiver;
    const consigneeName = consigneeParty?.name || '';
    const consigneeAddress = [
      consigneeParty?.address, consigneeParty?.city,
      consigneeParty?.state, consigneeParty?.pincode, consigneeParty?.country
    ].filter(Boolean).join(', ');

    // Build containers from docket container_details, fallback to empty
    const containers = containerDetails.length > 0
      ? containerDetails.map(c => ({
          container_no: c.container_no || '',
          size: c.container_type || c.size || '',
          l_seal_no: c.l_seal_no || '',
          no_of_packages: c.packages || '',
          gross_weight: c.gross_weight || '',
        }))
      : [{ ...EMPTY_CONTAINER }];

    setFormData({
      job_no: docket.system_docket_no || '',
      release_date: '',
      valid_date: '',
      subject: 'NOC / Delivery order',
      to: buildPartyText(resolved_parties?.shipping_line) || resolved_shipment?.carrier || '',
      igm_no: shipmentInfo.igm_no || '',
      igm_date: shipmentInfo.igm_date || '',
      mbl_no: shipmentInfo.mawb_no || '',
      mbl_date: shipmentInfo.mawb_date || '',
      hbl_no: shipmentInfo.hawb_no || '',
      hbl_date: shipmentInfo.hawb_date || '',
      containers,
      vessel_voyage: shipmentInfo.flight_no || '',
      consignee: consigneeName,
      address: consigneeAddress,
      port_of_load: resolved_shipment?.loading_from || '',
      port_of_discharge: resolved_shipment?.discharge_at || '',
      cargo_description: invoice.description || shipmentInfo.hawb_description || '',
      consignee_name: consigneeName,
      custom_broker_name: '',
    });
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const updateContainer = (idx, field, value) => {
    const updated = formData.containers.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    setFormData(prev => ({ ...prev, containers: updated }));
  };
  const addContainer = () => setFormData(prev => ({ ...prev, containers: [...prev.containers, { ...EMPTY_CONTAINER }] }));
  const removeContainer = (idx) => {
    if (formData.containers.length <= 1) return;
    setFormData(prev => ({ ...prev, containers: prev.containers.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`
        : `${backendUrl}/api/dockets/${docketId}/documents`;
      const method = isSaved ? 'PUT' : 'POST';
      const body = isSaved
        ? { document_data: formData }
        : { document_type: DOC_TYPE, document_data: formData };

      const res = await fetch(url, { method, headers: getAuth(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        const detail = Array.isArray(err.detail) ? err.detail.map(e => e.msg).join(', ') : err.detail || 'Save failed';
        throw new Error(detail);
      }
      const saved = await res.json();
      setDocumentId(saved.id);
      setIsSaved(true);
      toast({ title: 'Success', description: 'SEA Delivery Order saved successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const orig = document.title;
    document.title = `SEA_DO_${formData.job_no || docketId}`;
    setShowPrintView(true);
    setTimeout(() => { window.print(); setTimeout(() => { document.title = orig; }, 1000); }, 300);
  };

  if (showPrintView) {
    return (
      <div className="min-h-screen bg-white">
        <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between">
          <Button variant="outline" onClick={() => { document.title = 'LogisticsHub'; setShowPrintView(false); }} className="bg-white shadow-md">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Form
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="bg-white shadow-md">
              <Download className="w-4 h-4 mr-2" />Save as PDF
            </Button>
            <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 shadow-md">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        </div>
        <div className="pt-16">
          <SeaDeliveryOrderPrint ref={printRef} docketId={docketId} formData={formData} companyData={companyData} />
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  // Field helpers
  const F = ({ label, field, inputType = 'text', readOnly = false, placeholder = '' }) => (
    <div>
      <Label className="text-xs font-medium text-gray-500">{label}</Label>
      <Input type={inputType} value={formData[field] || ''} onChange={e => set(field, e.target.value)}
        readOnly={readOnly} placeholder={placeholder}
        className={`mt-1 h-9 text-sm ${readOnly ? 'bg-gray-50' : ''}`} data-testid={`sea-do-${field}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Ship className="w-5 h-5 text-teal-600" />SEA Delivery Order
              </h1>
              <p className="text-xs text-gray-500">Docket: {docketInfo?.docket?.system_docket_no || docketId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSaved && (
              <Button variant="outline" onClick={handlePrint} className="border-teal-600 text-teal-600 hover:bg-teal-50" data-testid="sea-do-download-btn">
                <Download className="w-4 h-4 mr-2" />Download DO
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="sea-do-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <Card className="bg-white p-5 space-y-5 rounded-xl border border-gray-200 shadow-sm">

          {/* Row 1: Job No | Release Date | Valid Date | Subject */}
          <div className="grid grid-cols-4 gap-4">
            <F label="Job No." field="job_no" readOnly />
            <F label="Release Date" field="release_date" inputType="date" />
            <F label="Valid Date" field="valid_date" inputType="date" />
            <F label="Subject" field="subject" />
          </div>

          {/* Row 2: To (wide) | IGM No | IGM Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-gray-500">To</Label>
              <Textarea value={formData.to || ''} onChange={e => set('to', e.target.value)}
                className="mt-1 h-24 text-sm" placeholder="Shipping Line / Recipient address"
                data-testid="sea-do-to" />
            </div>
            <div className="grid grid-cols-2 gap-4 content-start">
              <F label="IGM No." field="igm_no" />
              <F label="IGM Date" field="igm_date" inputType="date" />
            </div>
          </div>

          {/* Row 3: MBL No | MBL Date | HBL No | HBL Date */}
          <div className="grid grid-cols-4 gap-4">
            <F label="MBL No." field="mbl_no" />
            <F label="MBL Date" field="mbl_date" inputType="date" />
            <F label="HBL No." field="hbl_no" />
            <F label="HBL Date" field="hbl_date" inputType="date" />
          </div>

          {/* Container rows — repeatable */}
          <div className="space-y-3">
            {formData.containers.map((row, idx) => (
              <div key={idx} className="grid grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Container No.</Label>
                  <Input value={row.container_no} onChange={e => updateContainer(idx, 'container_no', e.target.value)}
                    className="mt-1 h-9 text-sm" placeholder="e.g. MSKU1234567" data-testid="sea-do-container-no" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Size</Label>
                  <Input value={row.size} onChange={e => updateContainer(idx, 'size', e.target.value)}
                    className="mt-1 h-9 text-sm" placeholder="e.g. 20 Feet Container" data-testid="sea-do-size" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">L Seal No.</Label>
                  <Input value={row.l_seal_no} onChange={e => updateContainer(idx, 'l_seal_no', e.target.value)}
                    className="mt-1 h-9 text-sm" data-testid="sea-do-l-seal" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">No. of packages</Label>
                  <Input value={row.no_of_packages} onChange={e => updateContainer(idx, 'no_of_packages', e.target.value)}
                    className="mt-1 h-9 text-sm" data-testid="sea-do-packages" />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs font-medium text-gray-500">Gross Weight</Label>
                    <Input value={row.gross_weight} onChange={e => updateContainer(idx, 'gross_weight', e.target.value)}
                      className="mt-1 h-9 text-sm" data-testid="sea-do-gross-weight" />
                  </div>
                  {formData.containers.length > 1 && (
                    <button onClick={() => removeContainer(idx)} className="mb-0.5 text-red-400 hover:text-red-600 p-1" data-testid="sea-do-remove-container">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addContainer}
              className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs mt-1" data-testid="sea-do-add-container">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Container
            </Button>
          </div>

          {/* Row: Vessel/Voyage | Consignee | Address | Port of Load | Port of Discharge */}
          <div className="grid grid-cols-5 gap-4 items-start">
            <F label="Vessel/Voyage" field="vessel_voyage" />
            <F label="Consignee" field="consignee" />
            <div>
              <Label className="text-xs font-medium text-gray-500">Address</Label>
              <Textarea value={formData.address || ''} onChange={e => set('address', e.target.value)}
                className="mt-1 h-20 text-sm" data-testid="sea-do-address" />
            </div>
            <F label="Port of Load" field="port_of_load" />
            <F label="Port of Discharge" field="port_of_discharge" />
          </div>

          {/* Row: Cargo Description | Consignee Name | Custom Broker Name */}
          <div className="grid grid-cols-3 gap-4 items-start">
            <div>
              <Label className="text-xs font-medium text-gray-500">Cargo Description</Label>
              <Textarea value={formData.cargo_description || ''} onChange={e => set('cargo_description', e.target.value)}
                className="mt-1 h-20 text-sm" data-testid="sea-do-cargo-description" />
            </div>
            <F label="Consignee Name" field="consignee_name" />
            <F label="Custom broker Name" field="custom_broker_name" />
          </div>

        </Card>

        {isSaved && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-teal-700">
              <span className="font-semibold">Saved</span> — SEA Delivery Order linked to docket {docketInfo?.docket?.system_docket_no}
            </p>
            <span className="text-xs text-teal-600">ID: {documentId?.slice(0, 8)}...</span>
          </div>
        )}

      </div>
    </div>
  );
};

export default SeaDeliveryOrderForm;
